import React, { useEffect, useState } from "react";
import { apiClient } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { Users, DollarSign, ScanEye, ShieldAlert, TrendingUp, Building2, Plus, Loader2, X } from "lucide-react";

const Card = ({ label, value, icon: Icon, sub, testid }) => (
  <div data-testid={testid} className="border border-border bg-card p-5 rounded-lg">
    <div className="flex items-center justify-between">
      <div className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
      <Icon className="h-4 w-4 text-primary" />
    </div>
    <div className="font-display text-4xl mt-2">{value}</div>
    {sub && <div className="mono text-xs text-muted-foreground mt-1">{sub}</div>}
  </div>
);

const EmptyState = ({ title, hint, action }) => (
  <div className="text-center py-10 px-4">
    <div className="font-heading text-base text-muted-foreground">{title}</div>
    {hint && <div className="text-xs text-muted-foreground mt-1.5">{hint}</div>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export default function Dashboard() {
  const { t } = useLang();
  const [m, setM] = useState(null);
  const [projects, setProjects] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);

  const load = async () => {
    const api = apiClient();
    const [metrics, p] = await Promise.all([api.get("/dashboard/metrics"), api.get("/projects")]);
    setM(metrics.data); setProjects(p.data);
  };

  useEffect(() => { load(); }, []);

  const fmt = (n) => (n === null || n === undefined) ? "—" : new Intl.NumberFormat().format(n);
  const currency = (n) => {
    if (n === null || n === undefined) return "—";
    if (n === 0) return "₹0";
    return `₹${(n / 100000).toFixed(1)}L`;
  };

  const openNew = () => setOpenDialog(true);

  return (
    <div className="space-y-8 fade-in-up" data-testid="dashboard-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{t("overview")}</div>
          <h1 className="font-display text-5xl md:text-6xl mt-2 leading-tight">
            <span className="font-display-italic text-primary">{t("greeting")}</span>
          </h1>
        </div>
        <button onClick={openNew} data-testid="new-project-btn"
          className="btn-sharp inline-flex items-center gap-2 bg-foreground text-background px-5 py-2.5 font-medium">
          <Plus className="h-4 w-4" /> {t("new_project")}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card label={t("projects")} value={fmt(m?.projects)} icon={Building2}
          sub={m?.projects ? "Active + planned" : "No projects yet"} testid="metric-projects" />
        <Card label={t("active_workers")} value={fmt(m?.active_workers)} icon={Users}
          sub={m?.active_workers ? "Registered on site" : "None registered"} testid="metric-workers" />
        <Card label={t("budget_spent")} value={currency(m?.spent)} icon={DollarSign}
          sub={m?.total_budget ? `of ${currency(m?.total_budget)}` : "No budget set"} testid="metric-budget" />
        <Card label={t("safety_score")}
          value={m?.safety_score !== null && m?.safety_score !== undefined ? `${m.safety_score}%` : "—"}
          icon={ShieldAlert}
          sub={m?.incidents ? `${m.incidents} incident${m.incidents === 1 ? "" : "s"} logged` : "No incidents yet"}
          testid="metric-safety" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-border bg-card p-5 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{t("milestones")}</div>
              <div className="font-heading text-lg">{t("milestone_progress")}</div>
            </div>
            <TrendingUp className="h-4 w-4 text-primary" />
          </div>
          {projects.length === 0 ? (
            <EmptyState title={t("no_milestones")}
              hint={t("create_first_project_hint")}
              action={
                <button onClick={openNew} data-testid="empty-new-btn"
                  className="btn-sharp inline-flex items-center gap-2 border border-border px-4 py-2 text-sm hover:bg-muted">
                  <Plus className="h-4 w-4" /> {t("create_project")}
                </button>
              } />
          ) : (
            projects.map((p) => (
              <div key={p.id} className="mb-4">
                <div className="flex justify-between text-sm mb-1.5">
                  <span>{p.name}</span>
                  <span className="mono text-primary">{p.completion || 0}%</span>
                </div>
                <div className="h-1.5 bg-muted overflow-hidden rounded-full">
                  <div className="h-full bg-primary transition-all" style={{ width: `${p.completion || 0}%` }} />
                </div>
              </div>
            ))
          )}
        </div>

        <div className="border border-border bg-card p-5 rounded-lg">
          <div className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">{t("activity")}</div>
          <div className="font-heading text-lg mb-4">{t("recent_ai_signals")}</div>
          {(m?.ai_scans || 0) === 0 ? (
            <EmptyState title={t("no_ai_activity")}
              hint={t("no_ai_activity_hint")} />
          ) : (
            <div className="text-sm text-muted-foreground">
              {m.ai_scans} AI scan{m.ai_scans === 1 ? "" : "s"} completed.
            </div>
          )}
        </div>
      </div>

      <div className="border border-border bg-card p-5 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <div className="font-heading text-lg">{t("your_projects")}</div>
          <div className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{projects.length} {t("total")}</div>
        </div>
        {projects.length === 0 ? (
          <EmptyState title={t("no_projects")}
            hint={t("no_projects_hint")}
            action={
              <button onClick={openNew} data-testid="empty-project-btn"
                className="btn-sharp inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 text-sm">
                <Plus className="h-4 w-4" /> {t("new_project")}
              </button>
            } />
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {projects.map((p) => (
              <div key={p.id} className="border border-border p-4 rounded-md" data-testid={`project-card-${p.id}`}>
                <div className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{p.location}</div>
                <div className="font-heading text-lg mt-1">{p.name}</div>
                <div className="mt-3 text-sm">
                  Budget: <span className="mono text-primary">₹{((p.budget || 0) / 100000).toFixed(1)}L</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">{p.client || "—"}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {openDialog && <NewProjectDialog onClose={() => setOpenDialog(false)} onCreated={() => { setOpenDialog(false); load(); }} />}
    </div>
  );
}

const NewProjectDialog = ({ onClose, onCreated }) => {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: "", client: "", location: "", budget: "", target_date: "", description: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.client || !form.location) {
      toast.error("Fill name, client and location"); return;
    }
    setBusy(true);
    try {
      await apiClient().post("/projects", {
        name: form.name, client: form.client, location: form.location,
        budget: Number(form.budget) || 0,
        target_date: form.target_date || new Date().toISOString().slice(0, 10),
        description: form.description || "",
      });
      toast.success("Project created");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-background/70 backdrop-blur-sm p-4"
      onClick={onClose} data-testid="new-project-dialog">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="w-full max-w-lg border border-border bg-card rounded-xl p-6 space-y-3 card-lift">
        <div className="flex items-start justify-between">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{t("new_project")}</div>
            <h2 className="font-display text-3xl mt-1">{t("create_project_title")}</h2>
          </div>
          <button type="button" onClick={onClose} data-testid="dialog-close" className="p-1 hover:bg-muted rounded">
            <X className="h-5 w-5" />
          </button>
        </div>
        {[
          ["name", `${t("project_name")} *`],
          ["client", `${t("client_label")} *`],
          ["location", `${t("location_label")} *`],
          ["budget", `${t("budget_label")} (₹)`],
          ["target_date", `${t("target_date")} (YYYY-MM-DD)`],
        ].map(([k, l]) => (
          <div key={k}>
            <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{l}</label>
            <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              data-testid={`np-${k}`}
              className="w-full mt-1 border border-border bg-background px-3 py-2 text-sm rounded focus:outline-none focus:border-primary" />
          </div>
        ))}
        <div>
          <label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{t("description")}</label>
          <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="np-description"
            className="w-full mt-1 border border-border bg-background px-3 py-2 text-sm rounded focus:outline-none focus:border-primary" />
        </div>
        <button type="submit" disabled={busy} data-testid="np-submit"
          className="btn-sharp w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 font-medium disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          {busy ? t("creating") : t("create_project")}
        </button>
      </form>
    </div>
  );
};
